import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AssignmentType, Contragent, Option, SortOrder } from "tm-shared/types";

import { QueueTaskStatus } from "@/hooks/useAsyncTaskQueue";
import { FilterInputValue } from "@/types/common";
import {
    AddressVerifyModalSelectionPayload,
    MarketCreateModalSelectionPayload,
    NewMarketFormValues,
} from "@/types/form";
import { MarketCreateModalState, ModalState, ModalType } from "@/types/modals";
import { RequestQueueItem } from "@/types/requestQueue";

import { setMdTasksConfigProps, SetTaskFieldProps, UiState } from "./ui.types";

const initialState: UiState = {
    mdTasksConfig: {},

    assignments: {
        page: {
            filter: null,
            activeCollapseId: null,
        },
    },

    updateProductBalances: {
        query: "",
    },

    rejectProducts: {
        queryMap: {},
        selectedFilters: {},
    },

    saleAdditional: {
        query: "",
    },

    workPlan: {
        query: "",
        marketsSortOption: { value: "route", label: "по маршруту" },
        activeCollapse: null,
    },

    checkout: {
        activeCollapse: null,
    },

    modalsQueueList: [],
    lastDataRefreshAt: null,
    failedQueue: [],

    marketCreatePage: {
        newMarketFormValues: null,
        modal: null,
        modalSelectionPayload: null,
        optionCache: {},
        contragentsMap: {},
    },
    addressVerifyModalSelectionPayload: null,

    productsFilter: {
        selectedFilters: {},
        sort: {
            tag: null,
            order: SortOrder.Asc,
        },
        hideZeroProducts: false,
        showOnlyMML: false,
    },

    initFailedRequests: [],
};

export const UiSlice = createSlice({
    name: "ui",
    initialState,
    reducers: {
        resetUiState: () => initialState,
        // ----------------------- MD ---------------------------
        setMdTasksConfig: (
            state,
            { payload }: PayloadAction<setMdTasksConfigProps>,
        ) => {
            const { marketId, taskId } = payload;
            const marketConfig = (state.mdTasksConfig[marketId] ??= {});
            const taskConfig = (marketConfig[taskId] ??= {});

            if ("subtaskId" in payload && payload.subtaskId) {
                const subtasks = (taskConfig.subtasks ??= {});
                subtasks[payload.subtaskId] = {
                    ...subtasks[payload.subtaskId],
                    [payload.field ?? "activeItem"]: payload.value,
                };
            } else {
                const { field, value } = payload as SetTaskFieldProps;
                marketConfig[taskId] = {
                    ...taskConfig,
                    [field ?? "activeItem"]: value,
                };
            }
        },

        // ---------------------------- Assignments --------------

        setAssignmentActiveCollapseId: (
            state,
            action: PayloadAction<string | undefined>,
        ) => {
            state.assignments.page.activeCollapseId = action.payload ?? null;
        },

        setAssignmentsFilter: (
            state,
            action: PayloadAction<AssignmentType | null>,
        ) => {
            state.assignments.page.filter = action.payload;
        },

        // ---------------------------- Sale additional --------------

        setSaleQuery: (state, { payload }: PayloadAction<string>) => {
            state.saleAdditional.query = payload;
        },

        // ---------------------------- Reject Products --------------

        setRejectProductsSelectedFilters: (
            state,
            {
                payload,
            }: PayloadAction<{
                [tag: string]: (string | number | boolean)[];
            } | null>,
        ) => {
            if (payload === null) {
                state.rejectProducts.selectedFilters = {};
                return;
            }

            for (const [key, values] of Object.entries(payload)) {
                if (values.length === 0) {
                    delete state.rejectProducts.selectedFilters[key];

                    continue;
                }

                state.rejectProducts.selectedFilters[key] = values;
            }
        },

        setRejectProductsQuery: (
            state,
            { payload }: PayloadAction<{ taskId: string; query: string }>,
        ) => {
            state.rejectProducts.queryMap = {
                ...state.rejectProducts.queryMap,
                [payload.taskId]: payload.query,
            };
        },

        // ---------------------------- Update Product Balances --------------

        setUpdateProductBalancesQuery: (
            state,
            action: PayloadAction<string>,
        ) => {
            state.updateProductBalances.query = action.payload;
        },

        // ---------------------------- Work plan --------------

        setWorkPlanQuery: (state, action: PayloadAction<string>) => {
            state.workPlan.query = action.payload;
        },

        setWorkPlanActiveCollapse: (
            state,
            action: PayloadAction<string | null>,
        ) => {
            state.workPlan.activeCollapse = action.payload;
        },

        setMarketsSortOption: (
            state,
            {
                payload,
            }: PayloadAction<{
                value: string;
                label: string;
            }>,
        ) => {
            state.workPlan.marketsSortOption = payload;
        },

        // ---------------------------- Checkout --------------
        setCheckoutActiveCollapse: (
            state,
            action: PayloadAction<string | null>,
        ) => {
            state.checkout.activeCollapse = action.payload;
        },

        // ---------------------------- Modals queue --------------

        addModalToQueue: (state, action: PayloadAction<ModalState>) => {
            if (state.modalsQueueList.some((m) => m.id === action.payload.id))
                return;

            state.modalsQueueList.push(action.payload);
        },
        removeModalFromQueue: (state, { payload }: PayloadAction<string>) => {
            state.modalsQueueList = state.modalsQueueList.filter(
                (m) => m.id !== payload,
            );
        },
        clearReminderModals: (state) => {
            if (!state?.modalsQueueList.length) return;
            state.modalsQueueList = state.modalsQueueList.filter(
                (m) =>
                    m.type !== ModalType.UncompletedTasks &&
                    m.type !== ModalType.UncompletedCheckouts,
            );
        },

        // ---------------------------- Global cooldown--------------

        setLastDataRefreshAt(state, action: PayloadAction<number | null>) {
            state.lastDataRefreshAt = action.payload;
        },

        // -------------------- Failed Queue -------------------

        addFailedQueueItem: (
            state,
            action: PayloadAction<RequestQueueItem>,
        ) => {
            state.failedQueue.push({
                ...action.payload,
                status: QueueTaskStatus.Error,
            });
        },

        // ----------------------------- Create Market Page -----------------------------

        setNewMarketForm: (
            state,
            action: PayloadAction<NewMarketFormValues>,
        ) => {
            state.marketCreatePage.newMarketFormValues = action.payload;
        },

        setMarketCreatePageModal: (
            state,
            action: PayloadAction<MarketCreateModalState | null>,
        ) => {
            state.marketCreatePage.modal = action.payload;
        },

        setMarketCreatePageModalSelectionPayload: (
            state,
            action: PayloadAction<MarketCreateModalSelectionPayload | null>,
        ) => {
            state.marketCreatePage.modalSelectionPayload = action.payload;
        },

        setMarketCreatePageOptionsCache(
            state,
            action: PayloadAction<{
                fieldName: keyof NewMarketFormValues;
                options: Option[];
            }>,
        ) {
            const { fieldName, options } = action.payload;

            state.marketCreatePage.optionCache[fieldName] = options;
        },

        clearMarketCreatePageCache(state) {
            state.marketCreatePage.optionCache = {};
            state.marketCreatePage.contragentsMap = {};
        },

        setMarketCreatePageContragentsMap(
            state,
            action: PayloadAction<Record<string, Contragent>>,
        ) {
            state.marketCreatePage.contragentsMap = action.payload;
        },

        setAddressVerifyModalSelectionPayload: (
            state,
            action: PayloadAction<AddressVerifyModalSelectionPayload | null>,
        ) => {
            state.addressVerifyModalSelectionPayload = action.payload;
        },

        // ----------------------------- Products filter -----------------------------

        patchProductsSelectedFilters(
            state,
            action: PayloadAction<Record<string, FilterInputValue>>,
        ) {
            if (Object.keys(action.payload).length === 0) {
                state.productsFilter.selectedFilters = {};
            } else {
                state.productsFilter.selectedFilters = {
                    ...state.productsFilter.selectedFilters,
                    ...action.payload,
                };
            }
        },
        setProductsSelectedFilters(
            state,
            action: PayloadAction<Record<string, FilterInputValue>>,
        ) {
            state.productsFilter.selectedFilters = action.payload;
        },
        setProductsHideZero(state, action: PayloadAction<boolean>) {
            state.productsFilter.hideZeroProducts = action.payload;
        },
        setProductsShowOnlyMML(state, action: PayloadAction<boolean>) {
            state.productsFilter.showOnlyMML = action.payload;
        },
        setProductsSortTag: (state, action: PayloadAction<string | null>) => {
            state.productsFilter.sort.tag = action.payload;
        },
        setProductsSortOrder: (state, action: PayloadAction<SortOrder>) => {
            state.productsFilter.sort.order = action.payload;
        },

        // ----------------------------- initFailedRequests -----------------------------

        setInitFailedRequests: (state, action: PayloadAction<string[]>) => {
            state.initFailedRequests = action.payload;
        },
    },
});

export const {
    resetUiState,
    setMdTasksConfig,
    setAssignmentActiveCollapseId,

    // ---------------------------- Assignments --------------
    setAssignmentsFilter,

    // ---------------------------- Sale Page --------------
    setSaleQuery,

    // ---------------------------- Reject Page --------------
    setRejectProductsQuery,
    setRejectProductsSelectedFilters,

    // ---------------------------- Update Product Balances --------------
    setUpdateProductBalancesQuery,

    // ---------------------------- WorkPlan --------------
    setWorkPlanQuery,
    setWorkPlanActiveCollapse,
    setMarketsSortOption,

    // ---------------------------- Checkout --------------
    setCheckoutActiveCollapse,

    // ---------------------------- Modals queue --------------
    addModalToQueue,
    removeModalFromQueue,
    clearReminderModals,

    // ---------------------------- Global cooldown --------------
    setLastDataRefreshAt,

    // ---------------------------- FailedQueue --------------
    addFailedQueueItem,

    // ---------------------------- Create Market Page --------------
    clearMarketCreatePageCache,
    setMarketCreatePageModalSelectionPayload,
    setMarketCreatePageOptionsCache,
    setNewMarketForm,
    setMarketCreatePageModal,
    setMarketCreatePageContragentsMap,

    // ---------------------------- Address Verify --------------
    setAddressVerifyModalSelectionPayload,

    // ---------------------------- Products filter --------------
    patchProductsSelectedFilters,
    setProductsHideZero,
    setProductsShowOnlyMML,
    setProductsSortTag,
    setProductsSortOrder,
    setProductsSelectedFilters,

    // ---------------------------- initFailedRequests --------------
    setInitFailedRequests,
} = UiSlice.actions;

export const uiReducer = UiSlice.reducer;
